import { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";

/* Shared searchable dropdown. Options are `{ value, label }`.
 *
 * - Typing in the query box filters the option list by case-insensitive
 *   substring match against the WHOLE label (anywhere, not just prefix).
 * - `limit` (optional): cap visible options when no query is active.
 *   When the user types, the limit lifts so older entries can be found.
 * - Click outside closes the dropdown.
 * - Keyboard: ArrowDown/ArrowUp move the highlight, Enter selects the
 *   highlighted option, Escape closes. The active row auto-scrolls
 *   into view as the highlight moves.
 * - `multi` (optional): when true, `value` is treated as an array of
 *   selected option values; clicking or pressing Enter toggles each
 *   option without closing the dropdown. The trigger shows up to two
 *   labels comma-joined, otherwise "N selected". A "Clear all" row
 *   appears at the top of the list when at least one option is picked.
 * - `groups` (optional): instead of a flat `options` array, pass
 *   `[{ header: "Open", options: [...] }, { header: "Closed", options: [...] }]`.
 *   The dropdown renders a non-clickable section header above each group's
 *   options. Headers participate in filtering visually (a header hides
 *   when the user's query removes all of its options) but are NOT
 *   navigable via ArrowUp/Down or Enter — only the option rows are.
 *   When `groups` is supplied, `options` is ignored.
 * - `allowCustom` (optional, single-select only): when the typed query
 *   matches none of the options, a "+ Add '<query>'" row replaces
 *   "No results" and Enter commits the typed text itself as the value
 *   (not just a pick from `options`) — e.g. ShipmentRegistry.js's
 *   Grower Code, 2026-10-08, Arturo: "debo ver los growers asignados
 *   al shipper para seleccionar de ahi pero tambien agregar
 *   manualmente" — "ambos". Does nothing when `options` DOES contain a
 *   match for the typed text; the real option always wins over typing
 *   it out by hand. Off by default — every other existing SearchSelect
 *   in the app keeps picking only from its own options list.
 * - `nowrap` (optional): keeps each option's label on a single line
 *   (ellipsis if it overflows) instead of the default wrap-to-a-
 *   second-line behavior — e.g. the Grower Code list above, Arturo:
 *   "el dropdown... debe verse el nombre horizontalmente no vertical".
 * - `panelMinWidth` (optional, px number): the open dropdown panel is
 *   at least this wide, even when the trigger field itself is
 *   narrower — the trigger's own width never changes, only the
 *   panel that opens below it. Arturo (Grower Code, same field as
 *   `nowrap` above): "al abrir el drop, se abra mas ancho para que
 *   muestre el nombre del productor pero que el campo no se haga mas
 *   grande solo la ventana". */

/* 2026-10-21, Arturo: "aplica siempre la logica de que los dropdown se
   muestren completos aunque el campo no este tan ancho" — generalizes
   the fix above into a standing default for every SearchSelect in the
   app, not just the one field that originally asked for it. */
var DEFAULT_PANEL_MIN_WIDTH = 260;

export default function SearchSelect({ value, onChange, options, placeholder, style, error, limit, disabled, multi, groups, allowCustom, nowrap, panelMinWidth }) {
  /* When grouped, build a single flat options array (each option tagged
     with __group) so all the existing filter/select/keyboard logic keeps
     working unchanged. Group headers are interleaved at render time only. */
  var flatFromGroups = useMemo(function () {
    if (!groups) return null;
    var flat = [];
    groups.forEach(function (g) {
      (g.options || []).forEach(function (o) { flat.push({ ...o, __group: g.header || "" }); });
    });
    return flat;
  }, [groups]);
  var effectiveOptions = flatFromGroups || options || [];

  /* Normalize the multi-select value to an array of strings so internal
     comparisons are uniform. The single-select path keeps `value` as-is. */
  var multiArr = multi ? ((value || []).map(String)) : null;
  var [open, setOpen] = useState(false);
  var [query, setQuery] = useState("");
  var [activeIdx, setActiveIdx] = useState(-1);
  var listRef = useRef(null);
  var rowRefs = useRef([]);
  /* Dropdown panel renders through a portal to document.body, positioned
     with `fixed` coordinates read off the trigger's own bounding box —
     2026-09-18 (Arturo, from inside the new Fixed Pricing product
     table: "el dropdown del producto se abre dentro de la tabla y no
     me deja ver todos los productos"). Before this, the panel was a
     plain absolutely-positioned child of the trigger, which any
     scrollable/overflow:auto ancestor (a wide table with horizontal
     scroll, a DraggableModal body, etc.) clips — this affects EVERY
     SearchSelect in the app (REGLA DE DROPDOWNS: the one shared
     dropdown component), not just this one screen. */
  var triggerRef = useRef(null);
  var [coords, setCoords] = useState(null);
  /* 2026-10-21, Arturo: "aplica siempre la logica de que los dropdown
     se muestren completos aunque el campo no este tan ancho" — standing
     rule, not a per-field opt-in: every SearchSelect's open panel now
     has a built-in minimum width (DEFAULT_PANEL_MIN_WIDTH) regardless
     of whether the caller passes panelMinWidth, so a narrow trigger
     (e.g. a 160px PO# field) never forces its option rows — which can
     carry a full "PO# · date · product · cases · type" label — to
     cram/wrap into that same narrow panel. panelMinWidth still wins
     when a caller explicitly wants something wider. `left` is now
     clamped to the viewport so a wider-than-trigger panel anchored
     near the right edge doesn't run off-screen. */
  var recalcCoords = function () {
    if (!triggerRef.current) return;
    var r = triggerRef.current.getBoundingClientRect();
    var w = Math.max(r.width, panelMinWidth || DEFAULT_PANEL_MIN_WIDTH);
    var left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8));
    setCoords({ top: r.bottom, left: left, width: w });
  };

  /* Filter by `label` substring (case-insensitive). Options may
     ALSO carry an optional `search` string (extra keywords not in
     the visible label — e.g., a country dropdown that shows
     "🇲🇽 +52" but should match "México" / "Mexico" / "MX"). */
  var filtered = useMemo(function () {
    if (!query) return limit ? effectiveOptions.slice(0, limit) : effectiveOptions;
    var q = query.toLowerCase();
    return effectiveOptions.filter(function (o) {
      if (String(o.label || "").toLowerCase().includes(q)) return true;
      var alias = String(o.search || "").toLowerCase();
      return alias && alias.indexOf(q) >= 0;
    });
  }, [effectiveOptions, query, limit]);
  var selected = multi ? null : effectiveOptions.find(function (o) { return String(o.value) === String(value); });

  /* Toggle when multi, replace + close when single. Centralized so both
     mouse click and Enter key share the same behavior. */
  function pickOption(o) {
    if (multi) {
      var v = String(o.value);
      var next = multiArr.indexOf(v) >= 0 ? multiArr.filter(function (x) { return x !== v; }) : multiArr.concat([v]);
      onChange(next);
    } else {
      onChange(o.value); setOpen(false); setQuery("");
      /* 2026-10-16 (Arturo, ExpenseBillRegistry's Fee Type field —
         applies to every SearchSelect): "al finalmente seleccionarlo
         con click, si doy tab no me pasa al siguiente campo dentro
         del renglon, me para a un campo que esta detras de esa
         ventana" — the dropdown panel renders through a portal
         appended to document.body, so it sits nowhere near the
         trigger in actual DOM order. After a click pick, nothing had
         focus (the clicked row isn't focusable), so the NEXT Tab
         press fell back to the browser's default starting point
         (early in the document) instead of continuing from here.
         Returning focus to the trigger — which sits at its real
         visual position in the form, not in the portal — makes the
         user's very next Tab press advance correctly on its own. */
      if (triggerRef.current) triggerRef.current.focus();
    }
  }
  function isOptSelected(o) {
    return multi ? multiArr.indexOf(String(o.value)) >= 0 : String(o.value) === String(value);
  }
  var hasSelection = multi ? multiArr.length > 0 : !!value;
  function clearAll() {
    if (multi) onChange([]);
    else { onChange(""); setOpen(false); setQuery(""); }
  }
  /* Trigger label: comma-joined for 1–2 picks, "N selected" beyond that. */
  function triggerLabel() {
    if (multi) {
      if (multiArr.length === 0) return placeholder || "Select…";
      if (multiArr.length <= 2) {
        return effectiveOptions.filter(function (o) { return multiArr.indexOf(String(o.value)) >= 0; }).map(function (o) { return o.label; }).join(", ");
      }
      return multiArr.length + " selected";
    }
    /* When an option carries `triggerLabel`, render that compact form
       in the closed trigger instead of the full `label`. The dropdown
       option list still shows the descriptive `label` for picking. */
    if (!selected) {
      /* allowCustom: `value` may be free text the user typed that
         never existed in `options` — show it as-is rather than
         falling back to the placeholder and silently hiding it. */
      if (allowCustom && value) return value;
      return placeholder || "Select…";
    }
    return selected.triggerLabel != null && selected.triggerLabel !== "" ? selected.triggerLabel : selected.label;
  }

  useEffect(function () {
    if (!open) return;
    var handler = function (e) { if (e.target.closest && !e.target.closest("[data-searchselect]")) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return function () { document.removeEventListener("mousedown", handler); };
  }, [open]);

  /* Position the portal'd panel the instant it opens (useLayoutEffect
     so there's no flash at the wrong spot), then keep it glued to the
     trigger while scrolling anywhere (capture:true catches scroll
     inside a nested container like the product table, not just the
     window) or resizing — a DraggableModal can be dragged/resized
     while a dropdown is open too. */
  useLayoutEffect(function () {
    if (!open) { setCoords(null); return; }
    recalcCoords();
    var onScroll = function () { recalcCoords(); };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return function () {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* When the dropdown opens, pre-highlight the current selection if
     present; otherwise highlight the first option. Reset when query
     changes so the highlight tracks the visible list. */
  useEffect(function () {
    if (!open) { setActiveIdx(-1); return; }
    /* In multi mode there's no single "current" value to land on; just
       highlight the first option. Single mode pre-highlights the picked
       value so Enter immediately re-confirms it. */
    if (multi) { setActiveIdx(filtered.length > 0 ? 0 : -1); return; }
    var idx = filtered.findIndex(function (o) { return String(o.value) === String(value); });
    setActiveIdx(idx >= 0 ? idx : (filtered.length > 0 ? 0 : -1));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(function () {
    if (!open) return;
    if (activeIdx >= filtered.length) setActiveIdx(filtered.length > 0 ? 0 : -1);
  }, [filtered.length, open, activeIdx]);

  /* Auto-scroll the highlighted row into view as the user navigates. */
  useEffect(function () {
    if (!open || activeIdx < 0) return;
    var row = rowRefs.current[activeIdx];
    if (row && row.scrollIntoView) row.scrollIntoView({ block: "nearest" });
  }, [activeIdx, open]);

  /* 2026-10-16 (Arturo, applies to every SearchSelect per REGLA DE
     DROPDOWNS): Tab should behave like a real tab-through-fields
     keystroke — commit whatever's highlighted/typed (same as Enter)
     AND move on to the next field, in one press, instead of requiring
     a mouse click first. Scoped to real DOM order (via the trigger's
     own position), not the portal's position in the document. */
  function focusableElements() {
    var sel = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.prototype.slice.call(document.querySelectorAll(sel)).filter(function (el) { return el.getClientRects().length > 0; });
  }
  function focusAdjacent(fromEl, forward) {
    if (!fromEl) return;
    var list = focusableElements();
    var idx = list.indexOf(fromEl);
    if (idx === -1) return;
    var next = list[forward ? idx + 1 : idx - 1];
    if (next) next.focus();
  }

  function handleKeyDown(e) {
    if (!open) return;
    if (e.key === "Tab") {
      if (!multi) {
        if (activeIdx >= 0 && activeIdx < filtered.length) {
          pickOption(filtered[activeIdx]);
        } else if (allowCustom && query.trim()) {
          onChange(query.trim());
        }
        setOpen(false); setQuery("");
      } else {
        setOpen(false);
      }
      e.preventDefault();
      var trigger = triggerRef.current;
      var forward = !e.shiftKey;
      requestAnimationFrame(function () { focusAdjacent(trigger, forward); });
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (filtered.length === 0) return;
      setActiveIdx(function (i) { return (i + 1) % filtered.length; });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (filtered.length === 0) return;
      setActiveIdx(function (i) { return i <= 0 ? filtered.length - 1 : i - 1; });
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIdx >= 0 && activeIdx < filtered.length) {
        pickOption(filtered[activeIdx]);
      } else if (allowCustom && !multi && query.trim()) {
        onChange(query.trim()); setOpen(false); setQuery("");
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      if (triggerRef.current) triggerRef.current.focus();
    }
  }

  /* `background`/`border`/`borderRadius`/`boxShadow`/`minHeight` from
     `style` are meant for the trigger box itself (read explicitly
     below); keeping them out of the wrapper's own style avoids
     painting the same border/shadow twice (wrapper + trigger stacked,
     since the wrapper has no padding and is the trigger's exact
     size). Anything else a caller passes (width, margin, etc.) still
     lands on the wrapper. */
  var wrapperStyle = style ? { ...style } : {};
  delete wrapperStyle.background;
  delete wrapperStyle.border;
  delete wrapperStyle.borderRadius;
  delete wrapperStyle.boxShadow;
  delete wrapperStyle.minHeight;
  delete wrapperStyle.fontSize;

  return (
    <div data-searchselect="true" style={{ position: "relative", ...wrapperStyle }}>
      {/* 2026-10-20, Arturo (ShipmentRegistry.js Quality/Condition/
          Severe condition/Status columns): "pues no es verdad, en
          modo edicion se ve igual" — the trigger's own inline `color`
          (just fixed above) was being silently clobbered by a global
          `!important` rule in App.css (".dm-window--groundfresh
          [data-searchselect] > div:first-child { color: var(--color-
          text-secondary) !important }", added for plain capture
          fields app-wide) — !important always beats a non-!important
          inline style, no matter how specific. `data-color-override`,
          only set when a caller explicitly passes `style.color`, lets
          that CSS rule exclude this element instead of touching the
          rule's own intended behavior for every OTHER SearchSelect. */}
      <div
        ref={triggerRef}
        data-color-override={(style && style.color) ? "true" : undefined}
        tabIndex={disabled ? -1 : 0}
        onClick={function () { if (!disabled) { setOpen(!open); setQuery(""); } }}
        onKeyDown={function (e) {
          if (disabled || open) return;
          if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true); setQuery("");
          }
        }}
        style={{
          padding: "7px 8px", fontSize: (style && style.fontSize !== undefined) ? style.fontSize : 12,
          border: (style && style.border) ? style.border : "1px solid " + (error ? "#ef4444" : "var(--color-border)"),
          borderRadius: (style && style.borderRadius !== undefined) ? style.borderRadius : 5,
          /* Trigger uses style.background/border/boxShadow when the
             caller passed them (so required-but-empty fields can
             render with yellow/red validation tints, or a caller can
             give the trigger a 3D metal-plate look — see Shipments'
             Filters popover); otherwise the standard input look. */
          background: (style && style.background) ? style.background : "var(--color-bg-input)",
          boxShadow: (style && style.boxShadow) ? style.boxShadow : "none",
          /* 2026-10-20, Arturo (ShipmentRegistry.js, Pallet Manifest
             Quality/Condition/Status columns): "el font de good
             quality deberia ser verde" — the trigger's text color was
             hardcoded here, ignoring any `style.color` a caller
             passed (unlike background/border/boxShadow just above,
             which already respect caller overrides) — so a tinted-
             plate color scheme like TEXAS_QC_COLORS always silently
             lost its own text color to the default gray/muted value.
             Now consistent with the other properties: a caller's
             `style.color` wins, every other caller (no color passed)
             keeps the exact same default behavior as before. */
          color: (style && style.color) ? style.color : (disabled ? "var(--color-text-muted)" : (hasSelection ? "var(--color-text)" : "var(--color-text-muted)")),
          cursor: disabled ? "not-allowed" : "pointer",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          /* Default (no override): content-box, `minHeight: 18` — a
             floor, unchanged for every other module. A caller-supplied
             `style.minHeight` (2026-10-20, Arturo: "reduce el alto...
             de 32 a 30px") instead switches this trigger to
             border-box and sets `height` too, not just `minHeight` —
             the trigger's natural content height (padding + line
             height) already exceeds 18px worth of "floor", so a
             smaller minHeight alone never shrank anything; an actual
             `height` does, same way `inp`'s own border-box sizing
             already does for plain inputs next to it. */
          ...((style && style.minHeight !== undefined)
            ? { boxSizing: "border-box", height: style.minHeight, minHeight: style.minHeight }
            : { minHeight: 18 }),
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: (style && style.fontSize !== undefined) ? style.fontSize : 11 }}>{triggerLabel()}</span>
        <span style={{ fontSize: 8, color: "var(--color-text-muted)", marginLeft: 4 }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && !disabled && coords && createPortal(
        <div data-searchselect="true" style={{ position: "fixed", top: coords.top, left: coords.left, width: coords.width, zIndex: 100000, background: "var(--color-bg-card-solid)", border: "1px solid var(--color-border)", borderRadius: 6, marginTop: 2, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", maxHeight: 240, display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "4px 6px", borderBottom: "1px solid var(--color-border)" }}>
            <input
              autoFocus
              value={query}
              onChange={function (e) { setQuery(e.target.value); setActiveIdx(0); }}
              onKeyDown={handleKeyDown}
              placeholder={placeholder || "Search…"}
              style={{ width: "100%", padding: "5px 6px", fontSize: 11, border: "1px solid var(--color-border)", borderRadius: 4, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
            />
          </div>
          <div ref={listRef} style={{ flex: 1, overflowY: "auto" }}>
            {hasSelection && (
              <div onClick={clearAll} style={{ padding: "6px 10px", fontSize: 11, cursor: "pointer", color: "var(--color-text-muted)", fontStyle: "italic", borderBottom: "1px solid var(--color-border)" }}>
                {multi ? "Clear all" : (placeholder || "Clear")}
              </div>
            )}
            {(function () {
              /* Render options. If groups are in play, insert a non-clickable
                 header row whenever the __group changes. Headers are visual
                 only — keyboard nav still operates on filtered[] indexes. */
              var nodes = []; var seenGroup = null;
              filtered.forEach(function (o, i) {
                if (groups) {
                  var g = o.__group || "";
                  if (g !== seenGroup) {
                    seenGroup = g;
                    if (g) {
                      nodes.push(
                        <div key={"hdr-" + g + "-" + i} style={{ padding: "5px 10px 3px", fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", background: "var(--color-bg)", borderTop: nodes.length > 0 ? "1px solid var(--color-border)" : "none", userSelect: "none" }}>
                          {g}
                        </div>
                      );
                    }
                  }
                }
                var isSelected = isOptSelected(o);
                var isActive = i === activeIdx;
                var bg = isActive ? "rgba(42,122,111,0.15)" : (isSelected ? "rgba(42,122,111,0.08)" : "transparent");
                nodes.push(
                  <div
                    ref={function (el) { rowRefs.current[i] = el; }}
                    key={String(o.value)}
                    onClick={function () { pickOption(o); }}
                    onMouseEnter={function () { setActiveIdx(i); }}
                    style={{ padding: "6px 10px", fontSize: 11, cursor: "pointer", background: bg, fontWeight: isSelected ? 600 : 400, display: "flex", alignItems: "center", gap: 8 }}
                  >
                    {multi && (
                      <span style={{ width: 12, height: 12, borderRadius: 3, border: "1.5px solid " + (isSelected ? "var(--color-accent)" : "var(--color-border)"), background: isSelected ? "var(--color-accent)" : "transparent", color: "#fff", fontSize: 9, lineHeight: "10px", textAlign: "center", flexShrink: 0, fontWeight: 700 }}>{isSelected ? "✓" : ""}</span>
                    )}
                    {/* 2026-09-29 (Arturo: "al abrirlo no alcanco a ver
                        la descripcion completa del producto... esa
                        parte no se ve") — a long option label (e.g.
                        FixedPricingRegistry.js's product picker:
                        "Blackberry 12/6oz OG Premium GF Label") used
                        to get hard-truncated with an ellipsis whenever
                        the dropdown panel was only as wide as a narrow
                        trigger (a table-cell picker, in his case). Now
                        wraps to a second line instead of cutting text
                        off — applies to every SearchSelect in the app,
                        never hides the tail of an option's text again. */}
                    <span style={nowrap ? { flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } : { flex: 1, whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3 }}>{o.label}</span>
                  </div>
                );
              });
              return nodes;
            })()}
            {filtered.length === 0 && allowCustom && !multi && query.trim() && (
              <div onClick={function () { onChange(query.trim()); setOpen(false); setQuery(""); }} style={{ padding: "8px 10px", fontSize: 11, cursor: "pointer", color: "var(--color-accent)", fontWeight: 600 }}>
                {"+ Add “" + query.trim() + "”"}
              </div>
            )}
            {filtered.length === 0 && !(allowCustom && !multi && query.trim()) && (
              <div style={{ padding: "10px", fontSize: 11, color: "var(--color-text-muted)", textAlign: "center" }}>No results</div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
